/*
 Models converted in this file are deprecated.
 To be removed once old transport order service is replaced by transport order hub.
 Date of writing this: 2022-04-19
 */
import {
    Address,
    Buyer,
    Confirmation,
    Contact,
    CurrentTransportLeg,
    CurrentTransportLegParty,
    CustomerData,
    EndOfTransportChain,
    FreightForwarder,
    Shipment,
    ShipmentPriority,
    ShipmentStatus,
    ShippingParty,
    StartOfTransportChain,
    Supplier,
    TimeWindow,
    TransportOrder,
    TransportOrderStatus,
    TransportOrderType,
    VehicleType
} from "@/model/transportorder/transportOrder"
import {
    ExternalAddress,
    ExternalBuyer,
    ExternalContact,
    ExternalCurrentTransportLeg,
    ExternalCurrentTransportLegParty,
    ExternalEndOfTransportChain,
    ExternalFreightForwarder,
    ExternalFreightPayer,
    ExternalPartyExternalIdentifiers,
    ExternalPriority,
    ExternalShippingParty,
    ExternalStartOfTransportChain,
    ExternalSupplier,
    ExternalTransportOrder,
    ExternalTransportOrderBundle,
} from "@/client/transportorder-hub/model"
import { ExternalGoodsType } from "@/client/transportorder-hub/goods.model"
import { convertExternalFreight } from "./transportOrderGoodsConverter"
import {
    ExternalFreightForwarderConfirmation,
    ExternalFreightForwarderResponse,
    ExternalLoading as FreightForwarderResponseExternalLoading,
    ExternalUnloading as FreightForwarderResponseExternalUnloading,
    ExternalVehicleType,
} from "../client/transportorder-hub/FreightForwarderResponse.model"

const convertExternalPartyExternalIdentifiers = (src: ExternalPartyExternalIdentifiers) => ({
    duns: src.duns,
    plantCode: src.plant_code,
    supplierCode: src.supplier_code,
})

const convertExternalTimeWindow = (src: TimeWindow | undefined): TimeWindow | undefined => {
    return src ? {
        time_from: src.time_from,
        time_to: src.time_to
    } : undefined
}

const convertExternalShippingParty = (src: ExternalShippingParty): ShippingParty => ({
    name: src.name,
    address: convertExternalAddress(src.address),
    contact: convertExternalContact(src.contact),
    externalIdentifiers: convertExternalPartyExternalIdentifiers(src.external_identifiers),
})

const convertExternalCurrentTransportLegParty = (src: ExternalCurrentTransportLegParty): CurrentTransportLegParty => ({
    name: src.name,
    address: convertExternalAddress(src.address),
    externalIdentifiers: convertExternalPartyExternalIdentifiers(src.external_identifiers),
})

const convertExternalStartOfTransportChain = (src: ExternalStartOfTransportChain): StartOfTransportChain => ({
    consignor: convertExternalShippingParty(src.consignor),
    pickupRequestedAt: convertExternalTimeWindow(src.pickup_requested_at) as TimeWindow,
    accessCode: src.access_code,
    placeOfLoading: src.place_of_loading,
})

const convertExternalEndOfTransportChain = (src: ExternalEndOfTransportChain): EndOfTransportChain => ({
    consignee: convertExternalShippingParty(src.consignee),
    deliveryEstimatedAt: convertExternalTimeWindow(src.delivery_estimated_at),
    accessCode: src.access_code,
    placeOfDischarge: src.place_of_discharge,
})

const convertExternalCurrentTransportLeg = (src: ExternalCurrentTransportLeg): CurrentTransportLeg => ({
    placeOfDeparture: convertExternalCurrentTransportLegParty(src.place_of_departure),
    departureRequestedAt: convertExternalTimeWindow(src.departure_requested_at),
    placeOfArrival: convertExternalCurrentTransportLegParty(src.place_of_arrival),
    arrivalEstimatedAt: convertExternalTimeWindow(src.arrival_estimated_at),
})

const convertToShipmentStatus = (cancelled: boolean | undefined): ShipmentStatus => {
    return cancelled === true ? ShipmentStatus.CANCELLED : ShipmentStatus.UNPLANNED
}

const convertExternalPriority = (priority: ExternalPriority): ShipmentPriority => {
    return ShipmentPriority[priority.toString().toUpperCase()]
}

const convertExternalGoodsType: (goods_type: ExternalGoodsType) => TransportOrderType = (goods_type) => {
    if (goods_type === ExternalGoodsType.CARGO_EMPTIES) {
        return TransportOrderType.EMPTIES
    }
    return TransportOrderType.MATERIAL
}

const convertExternalFreightForwarderResponse = (src: ExternalFreightForwarderResponse): Confirmation => ({
    updatedAt: src.last_modified_at,
    asset: src.means_of_transport?.asset_id || src.means_of_transport?.license_plate ? {
        id: src.means_of_transport.asset_id,
        licensePlate: src.means_of_transport.license_plate,
        countryCode: src.means_of_transport.country_code,
    } : undefined,
    accessCodeAtLoading: src.loading?.access_code,
    accessCodeAtUnloading: src.unloading?.access_code,
    loadingTimeWindow: src.loading?.time_window ? {
        time_from: src.loading.time_window.time_from,
        time_to: src.loading.time_window.time_to,
    }: undefined,
    unloadingTimeWindow: src.unloading?.time_window ? {
        time_from: src.unloading.time_window.time_from,
        time_to: src.unloading.time_window.time_to,
    }: undefined,
    phoneNumber: src.driver?.phone_number,
    vehicleType: src.billing_information?.vehicle_type,
    twoDrivers: src.billing_information?.two_drivers,
    loadId: src.load_id,
    crossDockId: src.cross_dock?.id
})

export const convertConfirmation = (src: Confirmation): ExternalFreightForwarderConfirmation => ({
    type: "CONFIRMATION",
    transport_chain_reference: src.transportChainReference?.trim() ? src.transportChainReference : undefined,
    load_id: src.loadId,
    means_of_transport: src.asset ? {
        mode: "ROAD",
        asset_id: src.asset.id,
        license_plate: src.asset.licensePlate,
        country_code: src.asset.countryCode,
    }: undefined,
    billing_information: (src.vehicleType !== undefined || src.twoDrivers !== undefined) ? {
        vehicle_type: src.vehicleType !== undefined ? convertVehicleType(src.vehicleType) : undefined,
        two_drivers: src.twoDrivers,
    }: undefined,
    loading: convertToFreightForwarderResponseExternalLoadingOrUnloading(src.accessCodeAtLoading, src.loadingTimeWindow),
    unloading: convertToFreightForwarderResponseExternalLoadingOrUnloading(src.accessCodeAtUnloading, src.unloadingTimeWindow),
    driver: src.phoneNumber ? {
        phone_number: src.phoneNumber,
    } : undefined,
    cross_dock: src.crossDockId ? {
        id: src.crossDockId
    }: undefined
})

const convertVehicleType = (src: VehicleType): ExternalVehicleType => {
    switch (src) {
    case "PASSENGER_CAR":
        return ExternalVehicleType.PASSENGER_CAR
    case "VAN":
        return ExternalVehicleType.VAN
    case "LIGHT_TRUCK":
        return ExternalVehicleType.LIGHT_TRUCK
    case "MEDIUM_TRUCK":
        return ExternalVehicleType.MEDIUM_TRUCK
    }
}

const convertToFreightForwarderResponseExternalLoadingOrUnloading = (
    accessCode: string | undefined,
    timeWindow: TimeWindow | undefined
): FreightForwarderResponseExternalLoading | FreightForwarderResponseExternalUnloading | undefined => {
    if (accessCode === undefined && timeWindow === undefined) {
        return undefined
    }
    return {
        access_code: accessCode,
        time_window: timeWindow ? {
            // TODO: time window model can be reworked once we drop support for the old transport order service
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            time_from: timeWindow.time_from!,
            time_to: timeWindow.time_to,
        }: undefined
    }
}

const isEndOfTransportChain = (externalTransportOrder: ExternalTransportOrder): boolean => {
    const arrivalIdentifiers = externalTransportOrder.current_transport_leg.place_of_arrival.external_identifiers
    const consigneeIdentifiers = externalTransportOrder.end_of_transport_chain.consignee.external_identifiers

    if (arrivalIdentifiers.duns !== undefined && arrivalIdentifiers.duns === consigneeIdentifiers.duns) {
        return true
    }

    if (
        arrivalIdentifiers.supplier_code !== undefined &&
        arrivalIdentifiers.supplier_code === consigneeIdentifiers.supplier_code
    ) {
        return true
    }

    if (
        arrivalIdentifiers.plant_code !== undefined &&
        arrivalIdentifiers.plant_code === consigneeIdentifiers.plant_code
    ) {
        return true
    }

    return false
}

const isStartOfTransportChain = (externalTransportOrder: ExternalTransportOrder): boolean => {
    const departureIdentifiers = externalTransportOrder.current_transport_leg.place_of_departure.external_identifiers
    const consignorIdentifiers = externalTransportOrder.start_of_transport_chain.consignor.external_identifiers

    if (departureIdentifiers.duns !== undefined && departureIdentifiers.duns === consignorIdentifiers.duns) {
        return true
    }

    if (
        departureIdentifiers.supplier_code !== undefined &&
        departureIdentifiers.supplier_code === consignorIdentifiers.supplier_code
    ) {
        return true
    }

    if (
        departureIdentifiers.plant_code !== undefined &&
        departureIdentifiers.plant_code === consignorIdentifiers.plant_code
    ) {
        return true
    }

    return false
}

function convertAndAddPrefillsToConfirmation(externalTransportOrder: ExternalTransportOrder, goods_type: ExternalGoodsType): Confirmation {
    const freightForwarderResponse = externalTransportOrder.freight_forwarder_response
    if (freightForwarderResponse !== undefined) {
        return convertExternalFreightForwarderResponse(freightForwarderResponse)
    }

    const orderType = convertExternalGoodsType(goods_type)
    switch (orderType) {
    case TransportOrderType.MATERIAL:
        return convertAndAddPrefillsToConfirmationMaterial(externalTransportOrder)
    case TransportOrderType.EMPTIES:
        return convertAndAddPrefillsToConfirmationEmpties(externalTransportOrder)
    }
}

const convertAndAddPrefillsToConfirmationMaterial = (externalTransportOrder: ExternalTransportOrder): Confirmation => {
    const accessCodeAtUnloading = isEndOfTransportChain(externalTransportOrder)
        ? externalTransportOrder.end_of_transport_chain.access_code
        : undefined

    switch (externalTransportOrder.priority) {
    case ExternalPriority.REGULAR:
        return {
            accessCodeAtUnloading,
            loadingTimeWindow: externalTransportOrder.current_transport_leg.departure_requested_at
        }
    case ExternalPriority.URGENT:
        return {
            accessCodeAtUnloading,
            vehicleType: externalTransportOrder.proposed_vehicle_type,
            unloadingTimeWindow: externalTransportOrder.current_transport_leg.arrival_estimated_at,
            twoDrivers: externalTransportOrder.requires_two_drivers,
        }
    }
}

const convertAndAddPrefillsToConfirmationEmpties = (externalTransportOrder: ExternalTransportOrder): Confirmation => ({
    accessCodeAtLoading: isStartOfTransportChain(externalTransportOrder)
        ? externalTransportOrder.start_of_transport_chain.access_code
        : undefined,
    loadingTimeWindow: externalTransportOrder.current_transport_leg.departure_requested_at,
})

export const convertExternalTransportOrder = (src: ExternalTransportOrder, goods_type: ExternalGoodsType): Shipment => {
    return {
        external_identification: src.external_id,
        brand: src.brand ?? undefined,
        external_consignment_reference_number: src.external_consignment_reference_number,
        freight: convertExternalFreight(src.goods),
        processIndicator: src.process_indicator,
        id: src.id,
        buyer: convertExternalBuyer(src.buyer),
        supplier: convertExternalSupplier(src.supplier),
        freightForwarder: convertExternalFreightForwarder(src.freight_forwarder),
        startOfTransportChain: convertExternalStartOfTransportChain(src.start_of_transport_chain),
        endOfTransportChain: convertExternalEndOfTransportChain(src.end_of_transport_chain),
        currentTransportLeg: convertExternalCurrentTransportLeg(src.current_transport_leg),
        transport_order_id: src.bundle_id,
        status: convertToShipmentStatus(src.cancelled),
        confirmationDraft: convertAndAddPrefillsToConfirmation(src, goods_type),
        isConfirmed: src.freight_forwarder_response !== undefined,
        last_modified_date: src.last_modified_at,
        createdDate: src.external_order_created_date,
        proposedVehicleType: src.proposed_vehicle_type,
        priority: convertExternalPriority(src.priority),
        requiresTwoDrivers: src.requires_two_drivers,
        version: src.etag,
        isStartOfTransportChain: isStartOfTransportChain(src),
        isEndOfTransportChain: isEndOfTransportChain(src),
    }
}

const convertExternalFreightForwarder = (src: ExternalFreightForwarder): FreightForwarder => {
    return {
        name: src.name,
        duns: src.duns,
        accountId: src.account_id,
        supplierCode: src.supplier_code,
        address: convertExternalAddress(src.address)
    }
}

const convertExternalAddress = (src: ExternalAddress | undefined): Address | undefined => {
    return src ? {
        city: src.city,
        country: src.country,
        postal_code: src.postal_code,
        street: src.street
    } : undefined
}

const convertExternalContact = (src: ExternalContact | undefined): Contact | undefined => {
    return src ? {
        name: src.name,
        email: src.email,
        phone: src.phone,
    } : undefined
}

const convertFreightPayer = (external_freight_payer: ExternalFreightPayer | undefined): CustomerData | undefined => {
    return external_freight_payer && {
        name: external_freight_payer.name,
    }
}

const convertExternalTransportOrderBundleStatus = (externalTransportOrders: Array<ExternalTransportOrder>): TransportOrderStatus => {
    const cancelled = externalTransportOrders.every(order => order.cancelled)
    if (cancelled) {
        return TransportOrderStatus.CANCELLED
    }
    return TransportOrderStatus.UNPLANNED
}

const convertExternalBuyer = (external_buyer: ExternalBuyer | undefined): Buyer | undefined => {
    return external_buyer && {
        name: external_buyer.name,
        address: convertExternalAddress(external_buyer.address)
    }
}

const convertExternalSupplier = (external_supplier: ExternalSupplier): Supplier => {
    return {
        name: external_supplier.name,
        address: convertExternalAddress(external_supplier.address),
        externalIdentifiers: {
            duns: external_supplier.external_identifiers.duns,
            plantCode: external_supplier.external_identifiers.plant_code,
            supplierCode: external_supplier.external_identifiers.supplier_code,
        }
    }
}

const convertExternalBundleIdForEmpties = (external_bundle_id: string): string | undefined => {
    /*
    Original ticket of this logic: RIODISP-1758:
        The Leergutauftragsnummer (sometimes also called "interne Tournummer")
        is part of the RFF+AGK defined in VDA 4933 T2: AGK${LOLI, 10-stellig}${TOUR_NUMMER}.
     */
    const match = external_bundle_id.match(/^AGK.{10}(.+?)$/)
    if (match) {
        return match[1]
    } else {
        return external_bundle_id
    }
}

const getExternalOrderNumber = (external_bundle_id: string | undefined, goods_type: ExternalGoodsType): string | undefined => {
    if (external_bundle_id === undefined) {
        return undefined
    }
    const orderType = convertExternalGoodsType(goods_type)
    if (orderType === TransportOrderType.MATERIAL) {
        return external_bundle_id
    }
    return convertExternalBundleIdForEmpties(external_bundle_id)
}

export const convertExternalTransportOrderBundle = (src: ExternalTransportOrderBundle): TransportOrder => {
    const firstExternalTransportOrder = src._embedded.transport_orders[0]
    const externalGoodsType = firstExternalTransportOrder.goods.type
    return {
        _embedded: {
            shipments: src._embedded.transport_orders.map(
                it => convertExternalTransportOrder(it, externalGoodsType)
            )
        },
        customer_data: convertFreightPayer(firstExternalTransportOrder.freight_payer),
        buyer: convertExternalBuyer(firstExternalTransportOrder.buyer),
        created_at: src.created_at,
        external_order_created_date: src._embedded.transport_orders[0].external_order_created_date,
        external_bundle_id: src.external_bundle_id,
        id: src.id,
        orderType: convertExternalGoodsType(externalGoodsType),
        status: convertExternalTransportOrderBundleStatus(src._embedded.transport_orders),
        external_order_number: getExternalOrderNumber(src.external_bundle_id, externalGoodsType),
    }
}
